'use client';
import { useId, useState, type ComponentProps } from 'react';
import { missingRequired, requiredMessage, validateRequiredFields } from './validation';
import './feedback.css';

export function Input({ onChange, onBlur, onInvalid, 'aria-describedby': describedBy, ...props }: ComponentProps<'input'>) {
  const id = useId(), [touched, setTouched] = useState(false);
  const [nativeError, setNativeError] = useState({ value: '', message: '' });
  const missing = props.required && !props.disabled && !props.readOnly && (['checkbox', 'radio'].includes(props.type || '') ? !props.checked : props.type === 'file' ? false : missingRequired(props.value));
  const error = !props.disabled && ((nativeError.value === String(props.value ?? '') ? nativeError.message : '') || (touched && missing ? requiredMessage : ''));
  return <><input {...props} aria-invalid={error ? true : props['aria-invalid']} aria-describedby={[describedBy, error ? id : ''].filter(Boolean).join(' ') || undefined}
    onChange={event => { setNativeError({ value: '', message: '' }); onChange?.(event); }}
    onBlur={event => { setTouched(true); onBlur?.(event); }} onInvalid={event => { event.preventDefault(); setTouched(true); setNativeError({ value: event.currentTarget.value, message: event.currentTarget.validationMessage || requiredMessage }); onInvalid?.(event); }} />
    {error && <span id={id} className="whenly-field-error" role="alert">{error}</span>}</>;
}

export function Select({ onBlur, onInvalid, 'aria-describedby': describedBy, ...props }: ComponentProps<'select'>) {
  const id = useId(), [touched, setTouched] = useState(false);
  const error = touched && props.required && !props.disabled && missingRequired(props.value);
  return <><select {...props} aria-invalid={error ? true : props['aria-invalid']} aria-describedby={[describedBy, error ? id : ''].filter(Boolean).join(' ') || undefined}
    onBlur={event => { setTouched(true); onBlur?.(event); }} onInvalid={event => { event.preventDefault(); setTouched(true); onInvalid?.(event); }} />
    {error && <span id={id} className="whenly-field-error" role="alert">{requiredMessage}</span>}</>;
}

export function Textarea({ onBlur, onInvalid, 'aria-describedby': describedBy, ...props }: ComponentProps<'textarea'>) {
  const id = useId(), [touched, setTouched] = useState(false);
  const error = touched && props.required && !props.disabled && !props.readOnly && missingRequired(props.value);
  return <><textarea {...props} aria-invalid={error ? true : props['aria-invalid']} aria-describedby={[describedBy, error ? id : ''].filter(Boolean).join(' ') || undefined}
    onBlur={event => { setTouched(true); onBlur?.(event); }} onInvalid={event => { event.preventDefault(); setTouched(true); onInvalid?.(event); }} />
    {error && <span id={id} className="whenly-field-error" role="alert">{requiredMessage}</span>}</>;
}

export function Form({ onSubmit, onInvalidCapture, children, ...props }: ComponentProps<'form'>) {
  return <form {...props} onInvalidCapture={event => {
    event.currentTarget.querySelector<HTMLElement>(':invalid')?.focus();
    onInvalidCapture?.(event);
  }} onSubmit={event => {
    if (!validateRequiredFields(event.currentTarget)) { event.preventDefault(); return; }
    onSubmit?.(event);
  }}>{children}</form>;
}
